'use client';
import Link from 'next/link';
import { usePathname } from 'next/navigation';
import type { ReactNode } from 'react';
import LogoutButton from '@/components/logout-button';
import NotificationBell from '@/components/notification-bell';

interface StaffShellProps {
  children: ReactNode;
}

// Pages reachable WITHOUT a session. They must not show the staff navigation —
// a logged-out visitor resetting their password should not be looking at
// "Sign out" and "+ New Event".
const AUTH_PAGES = new Set(['/login', '/signup', '/forgot-password', '/reset-password']);

export default function StaffShell({ children }: StaffShellProps) {
  const pathname = usePathname();

  // On portal, auth, or public help routes, render children without staff nav.
  const isPortalRoute = pathname.startsWith('/portal');
  const isAuthPage = AUTH_PAGES.has(pathname);
  const isHelpPage = pathname === '/help' || pathname.startsWith('/help/');

  if (isPortalRoute || isAuthPage || isHelpPage) {
    return <>{children}</>;
  }

  return (
    <>
      {/* ── Staff Navigation ─────────────────────────────────────── */}
      <header className="no-print sticky top-0 z-50 bg-white border-b border-[hsl(243,20%,90%)]">
        <div className="max-w-7xl mx-auto px-6 h-20 flex items-center justify-between">
          {/* Logo lockup */}
          <Link href="/" className="flex items-center gap-3 group">
            {/* eslint-disable-next-line @next/next/no-img-element */}
            <img
              src="/images/noah-logo.png"
              alt="Noah's Ark Animal Workshop"
              className="h-16 w-auto"
              suppressHydrationWarning
            />
            <div className="border-l border-[hsl(243,20%,86%)] pl-3">
              <span className="block text-[13px] font-semibold text-[hsl(243,30%,12%)] leading-tight" style={{ fontFamily: "var(--font-heading)" }}>
                Event Builder
              </span>
              <span className="block text-[11px] text-[hsl(243,15%,52%)] leading-tight">
                Noah&apos;s Ark Animal Workshop
              </span>
            </div>
          </Link>

          {/* Nav links */}
          <nav className="flex items-center gap-1">
            <Link
              href="/"
              className="text-sm text-[hsl(243,15%,50%)] hover:text-[hsl(243,30%,12%)] px-3 py-2 rounded-lg hover:bg-[hsl(243,40%,97%)] transition-colors font-medium"
            >
              All Events
            </Link>
            <Link
              href="/manage/animals"
              className="text-sm text-[hsl(243,15%,50%)] hover:text-[hsl(243,30%,12%)] px-3 py-2 rounded-lg hover:bg-[hsl(243,40%,97%)] transition-colors font-medium"
            >
              Products
            </Link>
            <Link
              href="/manage/organizers"
              className="text-sm text-[hsl(243,15%,50%)] hover:text-[hsl(243,30%,12%)] px-3 py-2 rounded-lg hover:bg-[hsl(243,40%,97%)] transition-colors font-medium"
            >
              Organizers
            </Link>
            <Link
              href="/manage/workshop-photos"
              className="text-sm text-[hsl(243,15%,50%)] hover:text-[hsl(243,30%,12%)] px-3 py-2 rounded-lg hover:bg-[hsl(243,40%,97%)] transition-colors font-medium"
            >
              Workshop Photos
            </Link>
            <Link
              href="/manage/staff"
              className="text-sm text-[hsl(243,15%,50%)] hover:text-[hsl(243,30%,12%)] px-3 py-2 rounded-lg hover:bg-[hsl(243,40%,97%)] transition-colors font-medium"
            >
              Staff
            </Link>
            <Link
              href="/events/new"
              className="text-sm bg-[hsl(243,40%,52%)] text-white px-4 py-2 rounded-lg hover:bg-[hsl(243,40%,46%)] transition-colors font-semibold ml-1"
              style={{ fontFamily: "var(--font-heading)" }}
            >
              + New Event
            </Link>
            <div className="w-px h-5 bg-[hsl(243,20%,88%)] mx-1" />
            <NotificationBell />
            <LogoutButton />
          </nav>
        </div>
      </header>

      {/* ── Staff Page Content ─────────────────────────────────────── */}
      <main className="max-w-7xl mx-auto px-6 py-8">
        {children}
      </main>
    </>
  );
}
